import { useEffect, useRef } from 'react';

/**
 * Keyboard shortcuts. Keys are KeyboardEvent.key values, optionally prefixed with "Shift+"
 * (e.g. " ", "ArrowLeft", "Shift+ArrowLeft", "l", "+"). Shortcuts are ignored while typing in a
 * text field, and arrow keys are left to sliders and selects that have focus (design:
 * interactions.md).
 */
export function useHotkeys(
  bindings: Record<string, (() => void) | undefined>,
  enabled = true,
): void {
  const ref = useRef(bindings);
  useEffect(() => {
    ref.current = bindings;
  });

  useEffect(() => {
    if (!enabled) return;
    const isTextField = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      (el.isContentEditable ||
        el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLInputElement &&
          !['range', 'checkbox', 'radio', 'button'].includes(el.type)));
    const ownsArrows = (el: EventTarget | null) =>
      el instanceof HTMLSelectElement || (el instanceof HTMLInputElement && el.type === 'range');

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTextField(e.target)) return;
      if (e.key.startsWith('Arrow') && ownsArrows(e.target)) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const shifted = e.shiftKey ? ref.current[`Shift+${key}`] : undefined;
      const action = shifted ?? ref.current[key];
      if (!action) return;
      e.preventDefault();
      action();
    };
    // Space on a focused button would also "click" it on key-up; the shortcut already acted.
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ' && ref.current[' '] && e.target instanceof HTMLButtonElement)
        e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [enabled]);
}
